import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { apiErr } from "../lib/api";
import { useUploads } from "../context/UploadContext";
import { Card, Alert, btnSecondary, CostBadge } from "./ui-bits";
import { toast } from "sonner";
import { Wand2, Loader2, Smartphone, Download, Trash2, Youtube, CheckCircle2 } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "./ui/alert-dialog";

// Tarjeta compartida "Crear short" — usada en la pestaña Publicar (vista manual, TabDescarga)
// y en la pantalla de vídeo listo del flujo de 1 clic (OneClickProducer::PublishStage). Sondeo
// propio contra /shorts/status (no depende de que el padre exponga refreshProject): así sirve
// igual en los dos sitios sin acoplarse a cómo cada flujo refresca su propio estado.
//
// Cada short generado se guarda (no sustituye al anterior) — se listan todos, más reciente
// primero, mismo criterio que "Historial de versiones" para el vídeo largo (RenderHistory.jsx).
export default function ShortsCard({ project }) {
  const { t, i18n } = useTranslation();
  const { startShort } = useUploads();
  const projectId = project?.id;
  const [job, setJob] = useState(project?.short_job || {});
  const [shorts, setShorts] = useState(project?.shorts || []);
  const [starting, setStarting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [costSeconds, setCostSeconds] = useState(20);
  const [ytStatus, setYtStatus] = useState({}); // shortId -> {status, url, error}
  const active = job.status === "procesando";

  const ytFor = (sh) => ytStatus[sh.id] || sh.youtube_upload || { status: "idle" };

  useEffect(() => {
    setJob(project?.short_job || {});
    setShorts(project?.shorts || []);
  }, [project?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancel = false;
    api.post("/cost/estimate", { action: "short", project_id: projectId })
      .then(({ data }) => { if (!cancel && data?.seconds != null) setCostSeconds(data.seconds); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    if (!active || !projectId) return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/shorts/status`);
        setJob(data || {});
        if (data?.status === "completo" && data.output) {
          setShorts((prev) => (prev.some((s) => s.id === data.output.id) ? prev : [data.output, ...prev]));
        }
      } catch (e) { /* silencioso, se reintenta en el siguiente tick */ }
    }, 2000);
    return () => clearInterval(id);
  }, [active, projectId]);

  const start = async () => {
    setStarting(true);
    try {
      await api.post(`/projects/${projectId}/shorts/generate`);
      setJob({ status: "procesando", progress: 0 });
      // Píldora global en la cabecera: el sondeo local de esta tarjeta solo vive mientras la
      // pantalla está montada, así que sin esto cambiar de pestaña dejaba el short sin ninguna
      // señal visible aunque siguiera generándose en el servidor.
      startShort(projectId, project?.name);
    } catch (e) { toast.error(apiErr(e)); } finally { setStarting(false); }
  };

  useEffect(() => {
    const uploading = shorts
      .filter((sh) => (ytStatus[sh.id] || sh.youtube_upload || {}).status === "uploading")
      .map((sh) => sh.id);
    if (uploading.length === 0) return;
    const id = setInterval(async () => {
      for (const shortId of uploading) {
        try {
          const { data } = await api.get(`/projects/${projectId}/shorts/${shortId}/youtube/status`);
          setYtStatus((prev) => ({ ...prev, [shortId]: data }));
        } catch (e) { /* silencioso, se reintenta en el siguiente tick */ }
      }
    }, 3000);
    return () => clearInterval(id);
  }, [shorts, ytStatus, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const startYtUpload = async (sh) => {
    setYtStatus((prev) => ({ ...prev, [sh.id]: { status: "uploading" } }));
    try {
      await api.post(`/projects/${projectId}/shorts/${sh.id}/youtube/upload`, {
        title: project?.name ? `${project.name} #Shorts` : undefined,
        description: sh.script || undefined,
        privacy: "public",
      });
    } catch (e) {
      toast.error(apiErr(e));
      setYtStatus((prev) => ({ ...prev, [sh.id]: { status: "idle" } }));
    }
  };

  const doDelete = async (id) => {
    setConfirmId(null);
    setBusyId(id);
    try {
      await api.delete(`/projects/${projectId}/shorts/${id}`);
      setShorts((prev) => prev.filter((s) => s.id !== id));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusyId(null); }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(i18n.language === "en" ? "en-US" : "es-ES",
        { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch (e) { return iso; }
  };

  if (!projectId) return null;

  // Más reciente primero — project.shorts llega del backend en orden de inserción (el más
  // antiguo primero); el nuevo se antepone en memoria al completarse (arriba), pero un short
  // ya existente al cargar la página seguía apareciendo en orden de creación sin esto.
  const sortedShorts = [...shorts].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <Card className="p-6" data-testid="shorts-card">
      <div className="flex items-center gap-2 mb-1">
        <Smartphone className="text-blue-600" size={22} />
        <span className="text-lg font-bold text-slate-900">{t("render.shortsTitle")}</span>
      </div>
      <p className="text-base text-slate-500 mb-4">{t("render.shortsSub")}</p>

      {job.status === "error" && (
        <Alert type="error" testid="short-error">{t("render.shortsError", { error: job.error })}</Alert>
      )}

      <button data-testid="create-short-button" onClick={start} disabled={starting || active}
              className={`${btnSecondary} disabled:opacity-50`}>
        {active || starting ? <Loader2 className="animate-spin" size={20} /> : <Wand2 size={20} />}
        {shorts.length > 0 ? t("render.shortsRecreateBtn") : t("render.shortsCreateBtn")}
        <CostBadge dark seconds={costSeconds} tip={t("render.shortsCostTip")} />
      </button>

      {active && (
        <div className="mt-3">
          <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden mb-1.5">
            <div className="h-full bg-blue-600 rounded-full transition-all duration-500" style={{ width: `${job.progress || 0}%` }} />
          </div>
          <div className="text-sm text-slate-500">{(job.log?.[0] || "").replace(/^\[[^\]]*\]\s*/, "") || t("render.shortsGenerating")}</div>
        </div>
      )}

      {sortedShorts.length > 0 && (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {sortedShorts.map((sh) => {
            const busy = busyId === sh.id;
            const yt = ytFor(sh);
            return (
              <div key={sh.id} data-testid={`short-item-${sh.id}`}
                   className="rounded-lg border-2 border-slate-200 p-2 flex flex-col gap-1.5">
                <video src={sh.video_url} controls
                       className="rounded-md border border-slate-200 w-full" style={{ aspectRatio: "9/16" }} />
                <div className="text-[11px] text-slate-500">{fmtDate(sh.created_at)} · {sh.duration}s</div>
                <span data-testid={`short-status-${sh.id}`}
                      className={`inline-flex w-fit items-center px-2 py-0.5 rounded-full border-2 text-[11px] font-bold ${
                        yt.status === "done" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-slate-50 text-slate-500 border-slate-200"
                      }`}>
                  {yt.status === "done" ? t("render.shortsPublished") : t("render.shortsNotPublished")}
                </span>
                <div className="flex items-center gap-1.5">
                  <a data-testid={`short-download-${sh.id}`} href={sh.video_url} download title={t("render.shortsDownload")}
                     className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700">
                    <Download size={13} />
                  </a>
                  {yt.status === "done" ? (
                    <a data-testid={`short-yt-view-${sh.id}`} href={yt.url} target="_blank" rel="noreferrer" title={t("render.shortsYtDone")}
                       className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-emerald-50 border-2 border-emerald-300 text-emerald-700">
                      <CheckCircle2 size={13} />
                    </a>
                  ) : (
                    <button data-testid={`short-yt-upload-${sh.id}`} onClick={() => startYtUpload(sh)}
                            disabled={yt.status === "uploading"} title={yt.status === "error" ? yt.error : t("render.shortsYtUpload")}
                            className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-white border-2 border-red-200 text-red-600 hover:border-red-400 disabled:opacity-60">
                      {yt.status === "uploading" ? <Loader2 className="animate-spin" size={13} /> : <Youtube size={13} />}
                    </button>
                  )}
                  <button data-testid={`short-delete-${sh.id}`} onClick={() => setConfirmId(sh.id)} disabled={busy}
                          className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-white border-2 border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-300 disabled:opacity-60">
                    {busy ? <Loader2 className="animate-spin" size={13} /> : <Trash2 size={13} />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent data-testid="short-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("render.historyDelete")}</AlertDialogTitle>
            <AlertDialogDescription>{t("render.historyDeleteConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="short-delete-cancel">{t("render.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="short-delete-accept" onClick={() => doDelete(confirmId)} className="bg-red-600 hover:bg-red-700">
              {t("render.historyDelete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
